'use client';

import { useEffect, useRef } from 'react';
import { useUser } from '@/context/UserContext';

// Helper: check if user has accepted analytics cookies
function hasAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false;
  const consent = window.localStorage.getItem('zamzi-connect-cookie-consent');
  return consent === 'true' || consent === 'accepted' || consent === 'all';
}

interface Props {
  userId: string;
  viewerId: string | null;
  searchTerm?: string;
}

/**
 * Fires a single view-increment request to the API on mount.
 * Runs entirely client-side so the profile page can use ISR caching
 * without losing per-visit view counts.
 */
export function ProfileViewTracker({ userId, viewerId, searchTerm }: Props) {
  const { user } = useUser();
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    fetch(`/api/profile/${encodeURIComponent(userId)}/view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        viewerId: viewerId ?? null,
        viewerName: user?.displayName || user?.email || null,
        searchTerm: searchTerm ?? null,
      }),
    }).catch(() => {
      // Silently ignore — view tracking must never break the page
    });

    // Optionally fire Google Analytics/Ads event if consented
    if (hasAnalyticsConsent() && typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
      (window as any).gtag('event', 'profile_view', {
        profile_id: userId,
        viewer_id: viewerId || undefined,
        search_term: searchTerm || undefined,
        event_category: 'engagement',
        event_label: 'Profile View',
        non_interaction: true,
      });
    }
  }, [userId, viewerId, searchTerm, user]);

  return null;
}
